// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { _getEnvVariable, _getEnv } from '@imprivata-cloud/common';
// NOTE: This is temporary until feature flags have been properly implemented
import featureFlags from './featureFlags.json';

export type FeatureFlags = typeof featureFlags;

export enum ENVIRONMENT {
  local = 1,
  dev = 2,
  int = 3,
  eph = 4,
  stg = 5,
  prod = 6,
  prod_eu = 7,
  prod_au = 8,
}

function getCurrentHostname(): string {
  const { hostname, host } = window.location;
  if (hostname) {
    return hostname;
  }
  return host.split(':')[0];
}

export const TldToEnv: Record<string, ENVIRONMENT> = {
  au: ENVIRONMENT.prod_au,
  eu: ENVIRONMENT.prod_eu,
};

export function _getCurrentEnv(): ENVIRONMENT {
  const url = getCurrentHostname();
  const match = url.match(/\b(int|dev|stg)\b/);
  if (match) {
    return ENVIRONMENT[match[1] as keyof typeof ENVIRONMENT];
  }
  if (url.includes('localhost')) {
    return ENVIRONMENT.local;
  }
  // Check for TLD
  const tldMatch = url.match(/\.([a-z]+)$/i);
  const tld = tldMatch ? tldMatch[1] : null;
  return tld ? (TldToEnv[tld] ?? ENVIRONMENT.prod) : ENVIRONMENT.prod;
}

export interface AppEnvConfig {
  API_URL: string;
  ZIPKIN_TRACING_URL?: string;
  OTLP_TRACING_URL?: string;
  COLLECT_TRACING?: string;
  TEST_UTILITY_ENABLED?: boolean;
  FACTOR_SWITCH_ENABLED?: boolean;
  TRACING_SOLUTION_TYPE?: string;
}

let envConfig: AppEnvConfig;

export function _initAppConfig(
  window: Window,
  env: ImportMetaEnv,
): AppEnvConfig {
  const { apiUrl, tracingUrl } = _getEnv();
  const currentEnv = _getCurrentEnv();
  const currentEnvName = ENVIRONMENT[currentEnv];

  let features: Partial<AppEnvConfig> = featureFlags['prod'];

  if (Object.keys(featureFlags).includes(currentEnvName)) {
    features = featureFlags[currentEnvName as keyof FeatureFlags];
  }

  console.debug(
    '[AppAccess:appConfigUtils] feature flags: ',
    JSON.stringify({
      currentEnv: currentEnvName,
      features,
    }),
  );

  return {
    API_URL:
      apiUrl || (_getEnvVariable(window, env, 'API_URL', apiUrl) as string),
    ZIPKIN_TRACING_URL: _getEnvVariable(window, env, 'ZIPKIN_TRACING_URL'),
    OTLP_TRACING_URL: _getEnvVariable(
      window,
      env,
      'OTLP_TRACING_URL',
      tracingUrl,
    ),
    COLLECT_TRACING: _getEnvVariable(window, env, 'COLLECT_TRACING'),
    ...features,
  };
}

export default function getConfig(): AppEnvConfig {
  if (envConfig === undefined) {
    envConfig = _initAppConfig(window, import.meta.env);
  }
  return envConfig;
}
